import type { MetaFunction } from 'react-router'
import { Link } from 'react-router'
import { STATIC_PAGE_HEADERS } from '~/lib/cache.server'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'

export function headers() { return STATIC_PAGE_HEADERS }

export const meta: MetaFunction = () => [
  { title: 'Notification de violation de données — AfficheServices' },
  { name: 'description', content: "Procédure de notification en cas de violation de données personnelles chez AfficheServices, conformément aux articles 33 et 34 du RGPD." },
  { name: 'robots', content: 'noindex,follow' },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/violation-donnees' },
]

export default function ViolationDonnees() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="mx-auto max-w-3xl px-4 py-16 lg:px-6">
        <h1 className="text-3xl font-bold text-slate-900">Notification de violation de données</h1>
        <p className="mt-2 text-sm text-slate-500">Conformité RGPD — Articles 33 et 34 du Règlement (UE) 2016/679</p>

        <article className="prose prose-slate mt-8 max-w-none text-sm leading-relaxed">
          <h2>Notre engagement</h2>
          <p>
            AfficheServices s'engage à protéger vos données personnelles et à vous informer en cas de
            violation susceptible d'engendrer un risque élevé pour vos droits et libertés, conformément
            à l'article 34 du RGPD.
          </p>

          <h2>Qu'est-ce qu'une violation de données ?</h2>
          <p>
            Une violation de données personnelles est une faille de sécurité entraînant, de manière
            accidentelle ou illicite, la destruction, la perte, l'altération, la divulgation non
            autorisée de données personnelles, ou l'accès non autorisé à ces données.
          </p>
          <p>Exemples : intrusion dans nos systèmes, fuite de base de données, vol d'identifiants,
          erreur d'envoi d'email contenant des données personnelles.</p>

          <h2>Notre procédure interne</h2>
          <ol>
            <li>
              <strong>Détection et confinement</strong> — Nos systèmes de surveillance (logs, alertes,
              monitoring) détectent les incidents. L'équipe technique isole la faille dans les plus
              brefs délais.
            </li>
            <li>
              <strong>Évaluation du risque</strong> — Nous évaluons la nature des données concernées,
              le nombre de personnes touchées, les conséquences probables et les mesures correctives
              possibles.
            </li>
            <li>
              <strong>Notification à la CNIL</strong> — Si la violation est susceptible d'engendrer un
              risque pour les droits et libertés des personnes, nous notifions la CNIL dans les{' '}
              <strong>72 heures</strong> suivant la prise de connaissance de la violation (article 33).
            </li>
            <li>
              <strong>Notification aux personnes concernées</strong> — Si le risque est élevé, nous
              vous informons directement par email dans les meilleurs délais, en décrivant la nature
              de la violation, les données concernées, les conséquences probables et les mesures
              prises (article 34).
            </li>
            <li>
              <strong>Documentation et suivi</strong> — Chaque incident est documenté dans notre
              registre des violations, avec les faits, les effets et les mesures correctives.
            </li>
          </ol>

          <h2>Mesures de sécurité en place</h2>
          <ul>
            <li>Chiffrement des données en transit (TLS 1.3) et au repos (AES-256)</li>
            <li>Authentification renforcée (hachage bcrypt, limitation de tentatives)</li>
            <li>Séparation des environnements (production, staging, développement)</li>
            <li>Sauvegardes automatiques chiffrées (quotidiennes, rétention 30 jours)</li>
            <li>Mises à jour de sécurité appliquées sous 48h (dépendances critiques)</li>
            <li>Conteneurs Docker avec capabilities réduites (cap_drop: ALL) et utilisateur non-root</li>
            <li>Monitoring continu des logs applicatifs et système</li>
          </ul>

          <h2>Signaler une vulnérabilité</h2>
          <p>
            Si vous découvrez une faille de sécurité sur notre plateforme, nous vous encourageons à
            nous la signaler de manière responsable :
          </p>
          <p className="rounded-xl bg-slate-50 p-4">
            <strong>Email :</strong>{' '}
            <a href="mailto:securite@afficheservices.fr" className="text-primary">securite@afficheservices.fr</a><br />
            <strong>Objet :</strong> [Responsible Disclosure] Description de la vulnérabilité<br />
            Nous nous engageons à accuser réception sous 48h et à traiter le signalement sous 7 jours.
          </p>

          <h2>Contact DPO</h2>
          <p>
            Pour toute question relative à la protection de vos données ou pour exercer vos droits :
          </p>
          <ul>
            <li><strong>Email DPO :</strong> <a href="mailto:rgpd@afficheservices.fr" className="text-primary">rgpd@afficheservices.fr</a></li>
            <li><strong>Adresse postale :</strong> AfficheServices — DPO, France</li>
          </ul>

          <h2>Liens utiles</h2>
          <ul>
            <li><Link to="/droit-acces" className="text-primary hover:underline">Droit d'accès</Link></li>
            <li><Link to="/droit-opposition" className="text-primary hover:underline">Droit d'opposition</Link></li>
            <li><Link to="/confidentialite" className="text-primary hover:underline">Politique de confidentialité</Link></li>
            <li><a href="https://www.cnil.fr/notifier-une-violation" target="_blank" rel="noreferrer" className="text-primary hover:underline">Notifier une violation à la CNIL</a></li>
          </ul>
        </article>
      </div>
      <Footer />
    </div>
  )
}
